import type { QueryResult } from '@apollo/client';
import { AUTH_STATUS, useAuthContext } from '@cosuno/authentication-frontend';
import { authFor, useAuth } from '@cosuno/authorization-frontend';
import { isCompanyFeatureEnabled } from '@cosuno/feature-flags-frontend';

import {
  type CombinedNotificationsQuery,
  type CombinedNotificationsQueryVariables,
  COMPANY_TYPE,
} from '~/__gql__/graphql';
import { usePollWhenVisible } from '~/shared/hooks/usePollWhenVisible';

import { queryCombinedNotifications } from './api';

export interface CombinedNotificationsResponse {
  combinedNotificationsResponse?: QueryResult<
    CombinedNotificationsQuery,
    CombinedNotificationsQueryVariables
  >;
}

const useCombinedNotifications = (): CombinedNotificationsResponse => {
  const { authState } = useAuthContext();
  const {
    results: { canViewMessages },
    loading: loadingAuth,
  } = useAuth({
    canViewMessages:
      authState.status === AUTH_STATUS.authenticated &&
      authFor('BidPackage').canI('view__messages').any(),
  });

  const includeMessageNotifications =
    authState.status === AUTH_STATUS.authenticated &&
    (authState.companyType !== COMPANY_TYPE.principal || canViewMessages);

  const includeTaskNotifications =
    authState.status === AUTH_STATUS.authenticated &&
    isCompanyFeatureEnabled(authState.user.company, 'feature-task-management');

  const combinedNotificationsResponse = usePollWhenVisible(queryCombinedNotifications, {
    variables: {
      offset: 0,
      limit: 10,
      includeMessageNotifications,
      includeTaskNotifications,
    },
    skip: authState.status !== AUTH_STATUS.authenticated || loadingAuth,
    pollInterval: 60 * 1000,
  });

  return { combinedNotificationsResponse };
};

export default useCombinedNotifications;
